import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Search, Filter, IndianRupee, Calendar, Plus, Eye, X, CreditCard, Users, Pencil, Check, Trash2, Receipt } from 'lucide-react';
import { api } from '../lib/api';
import { authService as auth } from '../lib/auth';

const OfflineAdmissions = () => {
  const [activeTab, setActiveTab] = useState('admissions');
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingFeeFilter, setPendingFeeFilter] = useState(false);
  
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [editingDate, setEditingDate] = useState(false);
  const [editDateValue, setEditDateValue] = useState('');
  const currentUser = auth.getCurrentUser();
  const isManagerOrAdmin = ['admin', 'manager', 'offline_manager'].includes(currentUser?.role?.toLowerCase());

  // Refund state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundData, setRefundData] = useState({
    amount: '', refund_mode: 'cash', utr_number: '', reason: '', refund_date: new Date().toISOString().split('T')[0]
  });

  // Old Fee Collection state
  const [oldFeeRecords, setOldFeeRecords] = useState([]);
  const [oldFeeTotal, setOldFeeTotal] = useState(0);
  const [oldFeeLoading, setOldFeeLoading] = useState(false);
  const [showOldFeeModal, setShowOldFeeModal] = useState(false);
  const [oldFeeDateFilter, setOldFeeDateFilter] = useState('');
  const [oldFeeSearch, setOldFeeSearch] = useState('');
  const [oldFeeForm, setOldFeeForm] = useState({
    student_name: '', phone: '', old_slip_number: '', amount: '',
    payment_mode: 'cash', utr_number: '', payment_date: new Date().toISOString().split('T')[0],
    course: '', notes: ''
  });
  
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    utr_number: '',
    receipt_number: '',
    notes: ''
  });

  const paymentModes = ['cash', 'upi', 'credit_card', 'debit_card', 'bank_transfer', 'cheque'];

  useEffect(() => {
    fetchAdmissions();
    fetchBatches();
  }, [batchFilter, statusFilter, pendingFeeFilter]);

  const fetchAdmissions = async () => {
    try {
      const params = {};
      if (batchFilter) params.batch_id = batchFilter;
      if (statusFilter) params.status = statusFilter;
      if (pendingFeeFilter) params.has_pending_fee = true;
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.getOfflineAdmissions(params);
      setAdmissions(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch admissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.getOfflineBatches({});
      setBatches(response.data || []);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await api.addOfflinePayment(selectedAdmission.id, {
        ...paymentData,
        amount: parseFloat(paymentData.amount)
      });
      toast.success('Payment added successfully');
      setShowPaymentModal(false);
      setPaymentData({ amount: '', payment_date: new Date().toISOString().split('T')[0], payment_mode: 'cash', utr_number: '', receipt_number: '', notes: '' });
      fetchAdmissions();
      
      // Refresh detail modal if open
      if (showDetailModal) {
        const response = await api.getOfflineAdmission(selectedAdmission.id);
        setSelectedAdmission(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment');
    }
  };

  const openRefundModal = (admission) => {
    setSelectedAdmission(admission);
    setRefundData({ amount: '', refund_mode: 'cash', utr_number: '', reason: '', refund_date: new Date().toISOString().split('T')[0] });
    setShowRefundModal(true);
  };

  const handleIssueRefund = async (e) => {
    e.preventDefault();
    if (!refundData.amount || parseFloat(refundData.amount) <= 0) {
      toast.error('Enter a valid refund amount');
      return;
    }
    if (refundData.refund_mode !== 'cash' && !refundData.utr_number) {
      toast.error('UTR number is required for online refunds');
      return;
    }
    try {
      await api.issueRefund(selectedAdmission.id, {
        ...refundData,
        amount: parseFloat(refundData.amount)
      });
      toast.success('Refund issued successfully');
      setShowRefundModal(false);
      fetchAdmissions();
      // Refresh detail if open
      if (showDetailModal) {
        const response = await api.getOfflineAdmission(selectedAdmission.id);
        setSelectedAdmission(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue refund');
    }
  };

  const openDetailModal = async (admission) => {
    try {
      const response = await api.getOfflineAdmission(admission.id);
      setSelectedAdmission(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load admission details');
    }
  };

  const openPaymentModal = (admission) => {
    setSelectedAdmission(admission);
    setShowPaymentModal(true);
  };

  const handleUpdateAdmissionDate = async () => {
    if (!editDateValue || !selectedAdmission) return;
    try {
      await api.updateOfflineAdmission(selectedAdmission.id, {
        created_at: new Date(editDateValue + 'T00:00:00').toISOString()
      });
      toast.success('Admission date updated');
      setEditingDate(false);
      fetchAdmissions();
      const response = await api.getOfflineAdmission(selectedAdmission.id);
      setSelectedAdmission(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update date');
    }
  };

  // ===== Old Fee Collection Handlers =====
  const fetchOldFeeRecords = async () => {
    setOldFeeLoading(true);
    try {
      const params = {};
      if (oldFeeDateFilter) { params.start_date = oldFeeDateFilter; params.end_date = oldFeeDateFilter; }
      if (oldFeeSearch) params.search = oldFeeSearch;
      const res = await api.getOldFeeCollections(params);
      setOldFeeRecords(res.data.records || []);
      setOldFeeTotal(res.data.total_amount || 0);
    } catch (err) {
      toast.error('Failed to load old fee records');
    } finally {
      setOldFeeLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'old-fee') fetchOldFeeRecords();
  }, [activeTab, oldFeeDateFilter]);

  const handleOldFeeSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createOldFeeCollection({ ...oldFeeForm, amount: parseFloat(oldFeeForm.amount) });
      toast.success('Old fee entry recorded');
      setShowOldFeeModal(false);
      setOldFeeForm({ student_name: '', phone: '', old_slip_number: '', amount: '', payment_mode: 'cash', utr_number: '', payment_date: new Date().toISOString().split('T')[0], course: '', notes: '' });
      fetchOldFeeRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleDeleteOldFee = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await api.deleteOldFeeCollection(id);
      toast.success('Record deleted');
      fetchOldFeeRecords();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const formatMode = (m) => {
    if (!m) return 'Cash';
    if (m === 'upi') return 'UPI';
    if (m === 'credit_card') return 'Credit Card';
    if (m === 'debit_card') return 'Debit Card';
    return m.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const filteredAdmissions = admissions.filter(a => 
    a.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.phone?.includes(searchTerm) ||
    a.admission_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalStats = {
    totalFee: admissions.reduce((sum, a) => sum + (a.final_fee || 0), 0),
    collected: admissions.reduce((sum, a) => sum + (a.amount_paid || 0), 0),
    pending: admissions.reduce((sum, a) => sum + (a.amount_pending || 0), 0)
  };

  return (
    <div className="space-y-6" data-testid="offline-admissions-page">
      {/* Header with Tabs */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Admissions</h1>
          <p className="text-slate-600">Manage student admissions and fee collection</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="admission-tabs">
        <button
          onClick={() => setActiveTab('admissions')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition ${activeTab === 'admissions' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-admissions"
        >
          Current Admissions
        </button>
        <button
          onClick={() => setActiveTab('old-fee')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition ${activeTab === 'old-fee' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-old-fee"
        >
          Old Fee Collection
        </button>
      </div>

      {activeTab === 'admissions' && (<>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <IndianRupee className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Fee</p>
              <p className="text-xl font-bold text-slate-800">₹{totalStats.totalFee.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CreditCard className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Collected</p>
              <p className="text-xl font-bold text-green-600">₹{totalStats.collected.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <IndianRupee className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Pending</p>
              <p className="text-xl font-bold text-red-600">₹{totalStats.pending.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, phone, admission number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg"
              data-testid="search-admissions"
            />
          </div>
          <select
            value={batchFilter}
            onChange={(e) => setBatchFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
          >
            <option value="">All Batches</option>
            {batches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="dropped">Dropped</option>
          </select>
          <label className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={pendingFeeFilter}
              onChange={(e) => setPendingFeeFilter(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-slate-600">Pending Fee Only</span>
          </label>
        </div>
      </div>

      {/* Admissions List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading...</div>
        ) : filteredAdmissions.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No admissions found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Admission #</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Student</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Batch</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Fee</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Paid</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Pending</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Next Payment</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAdmissions.map((admission) => (
                  <tr key={admission.id} className="hover:bg-slate-50" data-testid={`admission-row-${admission.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono font-medium text-teal-600">{admission.admission_number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{admission.student_name}</div>
                      <div className="text-sm text-slate-500">{admission.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
                        {admission.batch_name}
                      </span>
                      {admission.has_lifetime_validity && (
                        <span className="ml-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          Lifetime
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">₹{admission.final_fee?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-green-600 font-medium">₹{admission.amount_paid?.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${admission.amount_pending > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ₹{admission.amount_pending?.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {admission.next_payment_date || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDetailModal(admission)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                        {admission.amount_pending > 0 && (
                          <button
                            onClick={() => openPaymentModal(admission)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Add Payment"
                            data-testid={`add-payment-btn-${admission.id}`}
                          >
                            <Plus size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedAdmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
              <h2 className="text-lg font-semibold">Admission Details</h2>
              <button onClick={() => setShowDetailModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-6">
              {/* Student Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Admission Number</p>
                  <p className="font-mono font-bold text-teal-600 text-lg">{selectedAdmission.admission_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Student Name</p>
                  <p className="font-semibold text-slate-800">{selectedAdmission.student_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="text-slate-700">{selectedAdmission.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Batch</p>
                  <p className="text-slate-700">{selectedAdmission.batch_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Counsellor</p>
                  <p className="text-slate-700">{selectedAdmission.counsellor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Admission Date</p>
                  {editingDate && isManagerOrAdmin ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={editDateValue}
                        onChange={(e) => setEditDateValue(e.target.value)}
                        className="px-2 py-1 border border-slate-200 rounded text-sm"
                        data-testid="edit-admission-date"
                      />
                      <button onClick={handleUpdateAdmissionDate} className="p-1 text-green-600 hover:bg-green-50 rounded" data-testid="save-admission-date">
                        <Check size={16} />
                      </button>
                      <button onClick={() => setEditingDate(false)} className="p-1 text-slate-400 hover:bg-slate-100 rounded">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <p className="text-slate-700">{new Date(selectedAdmission.created_at).toLocaleDateString()}</p>
                      {isManagerOrAdmin && (
                        <button
                          onClick={() => {
                            setEditDateValue(selectedAdmission.created_at?.split('T')[0] || '');
                            setEditingDate(true);
                          }}
                          className="p-1 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded"
                          data-testid="edit-admission-date-btn"
                          title="Change admission date"
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Fee Summary */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Fee Summary</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600">Total Fee</p>
                    <p className="font-bold text-blue-800">₹{selectedAdmission.total_fee?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-600">Discount</p>
                    <p className="font-bold text-yellow-800">₹{selectedAdmission.discount?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Paid</p>
                    <p className="font-bold text-green-800">₹{selectedAdmission.amount_paid?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-600">Pending</p>
                    <p className="font-bold text-red-800">₹{selectedAdmission.amount_pending?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Payment History */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-slate-800">Payment History</h3>
                  {selectedAdmission.amount_pending > 0 && (
                    <button
                      onClick={() => { setShowDetailModal(false); openPaymentModal(selectedAdmission); }}
                      className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
                    >
                      <Plus size={16} /> Add Payment
                    </button>
                  )}
                  {isManagerOrAdmin && selectedAdmission.amount_paid > 0 && (
                    <button
                      data-testid="refund-btn"
                      onClick={() => { setShowDetailModal(false); openRefundModal(selectedAdmission); }}
                      className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 ml-2"
                    >
                      <IndianRupee size={14} /> Issue Refund
                    </button>
                  )}
                </div>
                {selectedAdmission.payments?.length === 0 ? (
                  <p className="text-slate-500 text-sm">No payments recorded yet</p>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-slate-600">Date</th>
                          <th className="px-3 py-2 text-left text-slate-600">Amount</th>
                          <th className="px-3 py-2 text-left text-slate-600">Mode</th>
                          <th className="px-3 py-2 text-left text-slate-600">UTR</th>
                          <th className="px-3 py-2 text-left text-slate-600">Receipt</th>
                          <th className="px-3 py-2 text-left text-slate-600">Collected By</th>
                          {isManagerOrAdmin && <th className="px-3 py-2 text-right text-slate-600"></th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedAdmission.payments?.map((payment, index) => (
                          <tr key={payment.id || index}>
                            <td className="px-3 py-2">{payment.payment_date}</td>
                            <td className="px-3 py-2 font-medium text-green-600">₹{payment.amount?.toLocaleString()}</td>
                            <td className="px-3 py-2 capitalize">{payment.payment_mode?.replace('_', ' ')}</td>
                            <td className="px-3 py-2 text-slate-600">{payment.utr_number || '-'}</td>
                            <td className="px-3 py-2">{payment.receipt_number || '-'}</td>
                            <td className="px-3 py-2">{payment.collected_by_name}</td>
                            {isManagerOrAdmin && (
                              <td className="px-3 py-2 text-right">
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Delete payment of ₹${payment.amount}?`)) return;
                                    try {
                                      await api.deletePayment(selectedAdmission.id, payment.id);
                                      toast.success('Payment deleted');
                                      fetchAdmissions();
                                      const res = await api.getOfflineAdmission(selectedAdmission.id);
                                      setSelectedAdmission(res.data);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Failed to delete');
                                    }
                                  }}
                                  className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Delete Payment"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Refund History */}
              {selectedAdmission.refunds?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-red-700 mb-3">Refund History</h3>
                  <div className="border border-red-200 rounded-lg overflow-hidden bg-red-50/30">
                    <table className="w-full text-sm">
                      <thead className="bg-red-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-red-700">Date</th>
                          <th className="px-3 py-2 text-left text-red-700">Amount</th>
                          <th className="px-3 py-2 text-left text-red-700">Mode</th>
                          <th className="px-3 py-2 text-left text-red-700">UTR</th>
                          <th className="px-3 py-2 text-left text-red-700">Reason</th>
                          <th className="px-3 py-2 text-left text-red-700">Processed By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100">
                        {selectedAdmission.refunds.map((ref, i) => (
                          <tr key={ref.id || i}>
                            <td className="px-3 py-2">{ref.refund_date}</td>
                            <td className="px-3 py-2 font-medium text-red-600">-₹{ref.amount?.toLocaleString()}</td>
                            <td className="px-3 py-2 capitalize">{ref.refund_mode?.replace('_', ' ')}</td>
                            <td className="px-3 py-2 text-slate-600">{ref.utr_number || '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{ref.reason}</td>
                            <td className="px-3 py-2">{ref.processed_by_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedAdmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Add Payment</h2>
              <button onClick={() => setShowPaymentModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 bg-slate-50 border-b">
              <p className="text-sm text-slate-600">{selectedAdmission.student_name}</p>
              <p className="font-semibold text-slate-800">Pending: ₹{selectedAdmission.amount_pending?.toLocaleString()}</p>
            </div>
            <form onSubmit={handleAddPayment} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                <input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  max={selectedAdmission.amount_pending}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date *</label>
                <input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                <select
                  value={paymentData.payment_mode}
                  onChange={(e) => setPaymentData({...paymentData, payment_mode: e.target.value, utr_number: ''})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  data-testid="payment-mode-select"
                >
                  {paymentModes.map(m => (
                    <option key={m} value={m}>{m === 'upi' ? 'UPI' : m === 'credit_card' ? 'Credit Card' : m === 'debit_card' ? 'Debit Card' : m.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              {paymentData.payment_mode && paymentData.payment_mode !== 'cash' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">UTR Number *</label>
                  <input
                    type="text"
                    value={paymentData.utr_number}
                    onChange={(e) => setPaymentData({...paymentData, utr_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    placeholder="Transaction reference number"
                    data-testid="utr-number-input"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Receipt Number</label>
                <input
                  type="text"
                  value={paymentData.receipt_number}
                  onChange={(e) => setPaymentData({...paymentData, receipt_number: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowPaymentModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                  Add Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </>)}

      {/* ===== Old Fee Collection Tab ===== */}
      {activeTab === 'old-fee' && (
        <div className="space-y-4">
          {/* Old Fee Summary */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-50 rounded-lg">
                  <IndianRupee size={24} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Old Fee Collected {oldFeeDateFilter ? `on ${new Date(oldFeeDateFilter).toLocaleDateString()}` : '(All Time)'}</p>
                  <p className="text-2xl font-bold text-green-700" data-testid="old-fee-total">₹{oldFeeTotal.toLocaleString()}</p>
                </div>
                <span className="text-sm text-slate-500 ml-4">{oldFeeRecords.length} entries</span>
              </div>
              <button
                onClick={() => setShowOldFeeModal(true)}
                className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
                data-testid="add-old-fee-btn"
              >
                <Plus size={18} /> New Entry
              </button>
            </div>
          </div>

          {/* Old Fee Filters */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text" placeholder="Search by name, phone, slip number..."
                  value={oldFeeSearch} onChange={(e) => setOldFeeSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchOldFeeRecords()}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg"
                  data-testid="search-old-fee"
                />
              </div>
              <input type="date" value={oldFeeDateFilter} onChange={(e) => setOldFeeDateFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg" data-testid="filter-date-old-fee" />
              {oldFeeDateFilter && (
                <button onClick={() => setOldFeeDateFilter('')} className="px-3 py-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Old Fee Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {oldFeeLoading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : oldFeeRecords.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Receipt size={48} className="mx-auto mb-3 text-slate-300" />
                <p>No old fee entries found</p>
                <p className="text-sm mt-1">Click "New Entry" to record a legacy fee payment</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Student</th>
                      <th className="px-4 py-3 text-left font-medium">Old Slip No.</th>
                      <th className="px-4 py-3 text-left font-medium">Amount</th>
                      <th className="px-4 py-3 text-left font-medium">Mode</th>
                      <th className="px-4 py-3 text-left font-medium">UTR</th>
                      <th className="px-4 py-3 text-left font-medium">Date</th>
                      <th className="px-4 py-3 text-left font-medium">Collected By</th>
                      {isManagerOrAdmin && <th className="px-4 py-3 text-left font-medium">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {oldFeeRecords.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{r.student_name}</p>
                          {r.phone && <p className="text-xs text-slate-500">{r.phone}</p>}
                          {r.course && <p className="text-xs text-slate-400">{r.course}</p>}
                        </td>
                        <td className="px-4 py-3 font-mono text-slate-700">{r.old_slip_number}</td>
                        <td className="px-4 py-3 font-semibold text-green-600">₹{r.amount?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-600">{formatMode(r.payment_mode)}</td>
                        <td className="px-4 py-3 text-slate-500">{r.utr_number || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{r.payment_date}</td>
                        <td className="px-4 py-3 text-slate-600">{r.collected_by_name}</td>
                        {isManagerOrAdmin && (
                          <td className="px-4 py-3">
                            <button onClick={() => handleDeleteOldFee(r.id)} className="text-red-500 hover:text-red-700 p-1">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Old Fee Entry Modal */}
      {showOldFeeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Record Old Fee Payment</h2>
              <button onClick={() => setShowOldFeeModal(false)} className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
            </div>
            <form onSubmit={handleOldFeeSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Student Name *</label>
                <input type="text" value={oldFeeForm.student_name}
                  onChange={(e) => setOldFeeForm({...oldFeeForm, student_name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" required data-testid="old-fee-student-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input type="tel" value={oldFeeForm.phone}
                    onChange={(e) => setOldFeeForm({...oldFeeForm, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Course</label>
                  <input type="text" value={oldFeeForm.course}
                    onChange={(e) => setOldFeeForm({...oldFeeForm, course: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Old Slip / Receipt Number *</label>
                <input type="text" value={oldFeeForm.old_slip_number}
                  onChange={(e) => setOldFeeForm({...oldFeeForm, old_slip_number: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Original receipt/slip number"
                  required data-testid="old-fee-slip-number" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                  <input type="number" value={oldFeeForm.amount}
                    onChange={(e) => setOldFeeForm({...oldFeeForm, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" required data-testid="old-fee-amount" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date *</label>
                  <input type="date" value={oldFeeForm.payment_date}
                    onChange={(e) => setOldFeeForm({...oldFeeForm, payment_date: e.target.value})}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                <select value={oldFeeForm.payment_mode}
                  onChange={(e) => setOldFeeForm({...oldFeeForm, payment_mode: e.target.value, utr_number: ''})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg">
                  {paymentModes.map(m => (<option key={m} value={m}>{formatMode(m)}</option>))}
                </select>
              </div>
              {oldFeeForm.payment_mode && oldFeeForm.payment_mode !== 'cash' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">UTR Number *</label>
                  <input type="text" value={oldFeeForm.utr_number}
                    onChange={(e) => setOldFeeForm({...oldFeeForm, utr_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Transaction reference number" required />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea value={oldFeeForm.notes}
                  onChange={(e) => setOldFeeForm({...oldFeeForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" rows={2} />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowOldFeeModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700" data-testid="old-fee-submit">Save Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedAdmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md" data-testid="refund-modal">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold text-red-700">Issue Refund</h2>
              <button onClick={() => setShowRefundModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 bg-red-50 border-b border-red-100">
              <p className="text-sm text-slate-700">Student: <strong>{selectedAdmission.student_name}</strong></p>
              <p className="text-sm text-slate-700">Amount Paid: <strong className="text-green-700">₹{selectedAdmission.amount_paid?.toLocaleString()}</strong></p>
            </div>
            <form onSubmit={handleIssueRefund} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Refund Amount *</label>
                <input
                  data-testid="refund-amount"
                  type="number"
                  value={refundData.amount}
                  onChange={e => setRefundData({ ...refundData, amount: e.target.value })}
                  max={selectedAdmission.amount_paid}
                  min={1}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Refund Mode *</label>
                <select
                  data-testid="refund-mode"
                  value={refundData.refund_mode}
                  onChange={e => setRefundData({ ...refundData, refund_mode: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-red-500"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="neft">NEFT</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
              {refundData.refund_mode !== 'cash' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">UTR Number *</label>
                  <input
                    data-testid="refund-utr"
                    type="text"
                    value={refundData.utr_number}
                    onChange={e => setRefundData({ ...refundData, utr_number: e.target.value })}
                    placeholder="Enter UTR / Transaction ID"
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
              )}
              {refundData.refund_mode === 'cash' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  Cash refund will be deducted from pending cash deposit amount.
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Refund Date *</label>
                <input
                  data-testid="refund-date"
                  type="date"
                  value={refundData.refund_date}
                  onChange={e => setRefundData({ ...refundData, refund_date: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason *</label>
                <textarea
                  data-testid="refund-reason"
                  value={refundData.reason}
                  onChange={e => setRefundData({ ...refundData, reason: e.target.value })}
                  placeholder="Reason for refund"
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              <button
                data-testid="submit-refund-btn"
                type="submit"
                className="w-full py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
              >
                Issue Refund
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineAdmissions;
